import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UrlFactory } from 'app/url.factory';
import { debounce } from 'lodash';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
    private asgardUrl: string;
    private eventHistory: any[] = [];
    private maxEventHistory = 15;
    private lastNavigation: string | null = null;
    private excludedErrors: string[] = ['location unavailable', 'Location permission was denied'];

    private scrollTimeout: any = null;
    private lastScrollY: number = 0;
    private isScrolling: boolean = false;
    private logErrorToServerDebounced: (error: any) => void;

    /** Clave para evitar múltiples reloads por error de chunks */
    private static readonly CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

    constructor(
        private http: HttpClient,
        private router: Router,
        private zone: NgZone,
        private _url: UrlFactory
    ) {
        this.asgardUrl = this._url.asgardUrl;
        this.logErrorToServerDebounced = debounce(this.logErrorToServer.bind(this), 5000); // Espera 5 segundos antes de enviar el error

        // Capturar eventos del usuario con más información
        this.trackUserEvents();

        // Capturar errores en Promises no manejadas
        window.addEventListener('unhandledrejection', (event) => {
            this.handleError(event.reason);
        });

        // Capturar cambios de ruta sin duplicaciones
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.recordNavigationEvent(event.urlAfterRedirects);
            }
        });
    }

    /** Captura errores globales con más contexto */
    handleError(error: any) {
        if (!error || typeof error !== 'object') return;

        const errorMessage = error.message || error.payload?.message || '';

        // Detectar errores de carga de módulos dinámicos (chunks)
        if (this.isChunkLoadError(errorMessage)) {
            this.handleChunkLoadError();
            return; // No enviar al servidor, se recargará la página
        }

        console.error('🚨 Captured Error:');
        console.log(error.payload, error.status, error.context, error.name);

        if (this.excludedErrors.some((e) => errorMessage?.includes(e))) return;

        const errorDetails = {
            message: error.payload?.message || error.message || 'Unknown error',
            stack: error.stack || '',
            url: error.context?.url || error?.url || window.location.href,
            method: error.context?.method || null,
            status: error.status || null,
            context: error.context || null,
            payload: error.payload || null,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            windowSize: `${window.innerWidth}x${window.innerHeight}`, // Tamaño de la ventana
            screenSize: `${screen.width}x${screen.height}`, // Tamaño de la pantalla física
            scrollPosition: window.scrollY, // Posición de scroll
            eventHistory: this.eventHistory, // Historial de eventos previos
        };

        console.error('Captured Error:', errorDetails);

        this.logErrorToServerDebounced(errorDetails);
    }

    /** Enviar el error al servidor */
    private logErrorToServer(errorDetails: any) {
        const environment = this._url.getEnvironment();
        if (environment === 'local') return;

        this.http.post(`${this.asgardUrl}/set-error`, errorDetails).subscribe({
            next: (res) => console.log('Error enviado al servidor:', res),
            error: (err) => console.error('Error enviando error:', err),
        });
    }

    /** Captura eventos del usuario con más información */
    private trackUserEvents() {
        const eventsToTrack = ['click', 'input', 'change'];

        eventsToTrack.forEach((eventType) => {
            document.addEventListener(
                eventType,
                (event: Event) => {
                    const target = event.target as HTMLElement;
                    if (!target) return;

                    const eventData: any = {
                        event: eventType,
                        element: target.tagName?.toLowerCase() || 'unknown',
                        id: target.id || null,
                        classes: (typeof target.className === 'string' ? target.className : target.classList?.toString()) || null,
                        text: target.textContent?.trim().slice(0, 50) || null,
                        location: window.location.pathname,
                        timestamp: Date.now(),
                        windowSize: `${window.innerWidth}x${window.innerHeight}`, // Tamaño de la ventana en el evento
                        scrollPosition: window.scrollY, // Capturar posición del scroll
                    };

                    // Si es un clic, agregar coordenadas y tamaño de pantalla
                    if (eventType === 'click') {
                        const mouseEvent = event as MouseEvent;
                        eventData['x'] = mouseEvent.clientX;
                        eventData['y'] = mouseEvent.clientY;
                        eventData['screenSize'] = `${screen.width}x${screen.height}`; // Tamaño de la pantalla física
                    }

                    this.recordEvent(eventData);
                },
                true
            );
        });

        // Capturar eventos de scroll con debounce
        window.addEventListener('wheel', (event: WheelEvent) => this.trackScroll(event));
    }

    /** Captura correctamente los cambios de navegación */
    private recordNavigationEvent(url: string) {
        if (this.lastNavigation === url) return; // Evita registros repetidos
        this.lastNavigation = url;

        const navigationEvent = {
            event: 'navigation',
            location: url,
            timestamp: Date.now(),
            windowSize: `${window.innerWidth}x${window.innerHeight}`, // Tamaño de la ventana en el evento
            screenSize: `${screen.width}x${screen.height}`, // Tamaño de la pantalla física
            scrollPosition: window.scrollY, // Posición de scroll en la navegación
        };

        this.recordEvent(navigationEvent);
    }

    /** Captura eventos de scroll con debounce */
    private trackScroll(event: WheelEvent) {
        if (!this.isScrolling) {
            this.isScrolling = true;
            this.recordEvent({
                event: 'scroll-start',
                location: window.location.pathname,
                timestamp: Date.now(),
                windowSize: `${window.innerWidth}x${window.innerHeight}`,
                scrollPosition: window.scrollY,
                deltaY: event.deltaY, // Captura el primer movimiento de scroll
            });
        }

        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
            this.recordEvent({
                event: 'scroll-end',
                location: window.location.pathname,
                timestamp: Date.now(),
                windowSize: `${window.innerWidth}x${window.innerHeight}`,
                scrollPosition: window.scrollY,
                deltaY: event.deltaY, // Captura la última posición después del desplazamiento
            });

            this.isScrolling = false;
        }, 500); // Espera 500ms después del último evento de scroll antes de registrar el final
    }

    /** Almacena eventos en un historial con un límite */
    private recordEvent(eventData: any) {
        if (this.eventHistory.length >= this.maxEventHistory) {
            this.eventHistory.shift(); // Eliminar el más antiguo
        }

        this.eventHistory.push(eventData);
    }

    /**
     * Detecta si el error es por carga de módulos dinámicos (chunks).
     * Esto ocurre cuando se hace deploy de una nueva versión y los chunks antiguos se eliminan.
     */
    private isChunkLoadError(errorMessage: string): boolean {
        if (!errorMessage) return false;

        const chunkErrorPatterns = ['Failed to fetch dynamically imported module', 'Loading chunk', 'ChunkLoadError', 'Loading CSS chunk'];

        return chunkErrorPatterns.some((pattern) => errorMessage.includes(pattern));
    }

    /**
     * Maneja errores de carga de chunks forzando un reload de la página.
     * Incluye protección contra loops infinitos de reload.
     */
    private handleChunkLoadError(): void {
        const lastReload = sessionStorage.getItem(GlobalErrorHandlerService.CHUNK_RELOAD_KEY);
        const now = Date.now();

        // Solo recargar si no se ha intentado en los últimos 10 segundos
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
            sessionStorage.setItem(GlobalErrorHandlerService.CHUNK_RELOAD_KEY, String(now));
            console.warn('[Cleverty] Nueva versión detectada. Recargando aplicación...');

            // Limpiar cache del service worker si existe
            if ('caches' in window) {
                caches.keys().then((names) => {
                    names.forEach((name) => caches.delete(name));
                });
            }

            // Forzar reload
            this.zone.runOutsideAngular(() => {
                window.location.reload();
            });
        } else {
            console.warn('[Cleverty] Reload ya intentado recientemente. Evitando loop.');
        }
    }
}
